import dayjs from "dayjs";

export function getEnumValues(enumType: any): any[] {
  return Object.values(enumType).filter(v => typeof v !== "number");
}

export function getEnumEntries(enumType: any): {value: string, label: string}[] {
  return Object.entries(enumType).map(([_, value]) => ({ value: value as string, label: value as string }));
}

export function compareString(a: string, b: string): number {
  return a.localeCompare(b);
}

export function compareTime(a: string, b: string): number {
  if (!a && !b) {
    return 0;
  }
  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }
  return new Date(a).getTime() - new Date(b).getTime();
}

export function isSubstring(sub: string, str: string): boolean {
  return str.trim().toLowerCase().includes(sub.trim().toLowerCase());
}

export function formatDateTime(time: string): string {
  return time ? new Date(time).toLocaleString('en-NZ') : '';
}

export function isPastTime(time: string): boolean {
  if (!time) {
    return false;
  }
  return new Date(time).getTime() < new Date().getTime();
}

export function formatDateTimeISO(time: string): string {
  return time ? new Date(time).toISOString() : '';
}

export function formatTime(time: string): string {
  return time
  ? new Date(time).toLocaleTimeString('en-NZ', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  : '';
}

export function getTimeRangeFromNow(daysAgo: number): [dayjs.Dayjs, dayjs.Dayjs] {
  const to = dayjs().hour(23).minute(59).second(59);
  const from = to.subtract(Math.max(0, daysAgo-1), 'day').hour(0).minute(0).second(0);
  return [from, to];
}

export function convertDayjsToISOString(t?: dayjs.Dayjs): string | undefined {
  if (!t) {
    return undefined;
  }
  return t.format('YYYY-MM-DDTHH:mm:ssZ');
}

export function convertCoordinateFromString(coordinate?: string): {lat: number, lng: number} | null {
  if (!coordinate) {
    return null;
  }
  const arr = coordinate.split(',');
  if (arr.length !== 2) {
    return null;
  }
  const lat = parseFloat(arr[0]);
  const lng = parseFloat(arr[1]);
  if (isNaN(lat) || isNaN(lng)) {
    return null;
  }
  return { lat, lng };
}

export function convertCoordinateToString(lat?: number, lng?: number): string {
  if (!lat || !lng) {
    return '';
  }
  return `${lat},${lng}`;
}

export function getTimeRange(secondsUntilNow: number): { from: string, to: string } {
  const now = new Date();
  const from = new Date(now.getTime() - secondsUntilNow * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

export function escapeCsvField(value: string | Date, opts: { delimiter?: string; quoteAlways?: boolean } = {}) {
  const delimiter = opts.delimiter ?? ',';
  const quoteAlways = opts.quoteAlways ?? false;

  let s;
  if (value === null || value === undefined) {
    s = '';
  } else if (value instanceof Date) {
    s = value.toISOString();
  } else {
    s = String(value);
  }

  // Check for special characters
  const hasQuote = s.includes('"');
  const hasDelimiter = s.includes(delimiter);
  const hasNewline = s.includes('\n') || s.includes('\r');
  const hasLeadingOrTrailingSpace = /^\s|\s$/.test(s);

  // Escape internal double quotes by doubling them
  if (hasQuote) {
    s = s.replace(/"/g, '""');
  }

  const mustQuote =
    quoteAlways || hasQuote || hasDelimiter || hasNewline || hasLeadingOrTrailingSpace;

  return mustQuote ? `"${s}"` : s;
}

export function getValueWithUnit(value: string | number | null | undefined, unit: string): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  return `${value} ${unit}`;
}
